import { SortableTableHead } from '@/components/ui/sortable-table-head'
import { useSortingState } from '@/hooks/useSortingState'
import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Search, Gift, ExternalLink } from 'lucide-react'

/**
 * Lista delle "Box Figlie" (TipologiaBox con riutilizzabile_come_componente=True).
 * Punto 14 del feedback cliente: il cliente vuole vedere la giacenza delle
 * scatoline pronte (mandorle, cookies, roccocò...) dove si aspetta di trovarla,
 * ovvero nella sezione Prodotti.
 */
export default function BoxFiglieList() {
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useSortingState('box-figlie', 'nome')

  const { page, setPage } = useListPagination({ search, ordering })

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['box-figlie', { page, search, ordering }],
    queryFn: () =>
      apiClient
        .get('/box/tipologie/', {
          params: {
            page,
            ordering,
            riutilizzabile_come_componente: true,
            attivo: true,
            search: search || undefined,
          },
        })
        .then((r) => r.data),
  })

  const boxFiglie = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []

  const formatCurrency = (cents) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-violet-600" />
            Box Figlie (scatoline pronte)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Tipologie box marcate come &quot;riutilizzabile come componente&quot;. Sono le
            scatoline gi&agrave; assemblate che possono essere contenute in box pi&ugrave; grandi.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome o codice..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Caricamento...</div>
          ) : boxFiglie.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Nessuna box figlia trovata. Marca una TipologiaBox come &quot;riutilizzabile
              come componente&quot; per vederla qui.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="codice" currentOrdering={ordering} onSort={setOrdering}>Codice</SortableTableHead>
                  <SortableTableHead field="nome" currentOrdering={ordering} onSort={setOrdering}>Nome</SortableTableHead>
                  <SortableTableHead field="giacenza_disponibile" currentOrdering={ordering} onSort={setOrdering} className="text-right">Giacenza</SortableTableHead>
                  <SortableTableHead field="giacenza_riservata" currentOrdering={ordering} onSort={setOrdering} className="text-right">Riservata</SortableTableHead>
                  <SortableTableHead field="costo_totale_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">Costo</SortableTableHead>
                  <SortableTableHead field="prezzo_vendita_cents" currentOrdering={ordering} onSort={setOrdering} className="text-right">Prezzo vendita</SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {boxFiglie.map((box) => (
                  <TableRow key={box.id}>
                    <TableCell className="font-mono">{box.codice}</TableCell>
                    <TableCell className="font-medium">{box.nome}</TableCell>
                    <TableCell className="text-right">
                      {box.giacenza?.quantita_disponibile ?? 0}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {box.giacenza?.quantita_riservata ?? 0}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(box.costo_totale_cents)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(box.prezzo_vendita_cents)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/box/tipologie/${box.id}`}>
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Apri scheda
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <ListPagination data={data} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
